import { useSummaryIncomeStatementData } from "../hooks/sections/useSummaryIncomeStatementData";
import { useSummaryBalanceSheetData } from "../hooks/sections/useSummaryBalanceSheetData";
import { MemoTable } from "./MemoTable";
import { PeriodSelector } from "./PeriodSelector";
import { DeltaIndicator } from "./DeltaIndicator";
import {
    incomeStatementResolutionAtom,
    balanceSheetResolutionAtom,
    incomeStatementMetricsAtom,
    balanceSheetMetricsAtom,
} from "../metrics.state";
import { InformationCircleIcon } from "@heroicons/react/24/solid";
import { ColDef, ICellRendererParams } from "ag-grid-community";
import { useMemo, useCallback } from "react";
import { useSetAtom, useAtomValue } from "jotai";
import { selectedMetricForLineageAtom } from "../state";
import { rightDrawerOpenAtom } from "@/features/Navigation/state";

function DeltaCellRenderer(params: ICellRendererParams) {
    const delta = params.value as number | null;
    return <DeltaIndicator delta={delta} />;
}

export function SummaryFinancials() {
    const incomeStatementResult = useSummaryIncomeStatementData();
    const balanceSheetResult = useSummaryBalanceSheetData();
    const incomeStatementMetrics = useAtomValue(incomeStatementMetricsAtom);
    const balanceSheetMetrics = useAtomValue(balanceSheetMetricsAtom);
    const setSelectedMetricForLineage = useSetAtom(
        selectedMetricForLineageAtom,
    );
    const setRightDrawerOpen = useSetAtom(rightDrawerOpenAtom);

    const handleIncomeStatementCellClick = useCallback(
        (params: any) => {
            const field = params.colDef.field;
            if (
                field === "metric" ||
                field === "delta" ||
                field === "yoyChange"
            )
                return;

            const metricLabel = params.data.metricLabel;
            const metricName = params.data.metric;

            if (!metricLabel) return;

            const metric = incomeStatementMetrics[metricLabel];

            if (!metric) {
                return;
            }

            setSelectedMetricForLineage({
                metric,
                period: field,
                title: metricName,
            });
            setRightDrawerOpen(true);
        },
        [
            incomeStatementMetrics,
            setSelectedMetricForLineage,
            setRightDrawerOpen,
        ],
    );

    const handleBalanceSheetCellClick = useCallback(
        (params: any) => {
            const field = params.colDef.field;
            if (
                field === "metric" ||
                field === "delta" ||
                field === "periodChange"
            )
                return;

            const metricLabel = params.data.metricLabel;
            const metricName = params.data.metric;

            if (!metricLabel) return;

            const metric = balanceSheetMetrics[metricLabel];

            if (!metric) {
                return;
            }

            setSelectedMetricForLineage({
                metric,
                period: field,
                title: metricName,
            });
            setRightDrawerOpen(true);
        },
        [balanceSheetMetrics, setSelectedMetricForLineage, setRightDrawerOpen],
    );

    const incomeStatementColumns = useMemo<ColDef[]>(() => {
        return incomeStatementResult.columns.map((col, index) => {
            if (index === 0) {
                return {
                    field: col.field,
                    headerName: col.headerName,
                    pinned: "left",
                    flex: 1.5,
                    minWidth: 140,
                    cellClass: "heading-s",
                };
            }

            if (index === incomeStatementResult.columns.length - 1) {
                return {
                    field: col.field,
                    headerName: col.headerName,
                    pinned: "right",
                    flex: 1,
                    maxWidth: 120,
                    cellRenderer: DeltaCellRenderer,
                };
            }

            return {
                field: col.field,
                headerName: col.headerName,
                flex: 1,
                minWidth: 140,
                cellClass: "cursor-pointer hover:bg-selected transition-colors",
            };
        });
    }, [incomeStatementResult.columns]);

    const balanceSheetColumns = useMemo<ColDef[]>(() => {
        return balanceSheetResult.columns.map((col, index) => {
            if (index === 0) {
                return {
                    field: col.field,
                    headerName: col.headerName,
                    flex: 1.5,
                    pinned: "left",
                    minWidth: 140,
                    cellClass: "heading-s",
                };
            }

            if (index === balanceSheetResult.columns.length - 1) {
                return {
                    field: col.field,
                    headerName: col.headerName,
                    pinned: "right",
                    flex: 1,
                    minWidth: 120,
                    cellRenderer: DeltaCellRenderer,
                };
            }

            return {
                field: col.field,
                headerName: col.headerName,
                flex: 1,
                minWidth: 120,
                cellClass: "cursor-pointer hover:bg-selected transition-colors",
            };
        });
    }, [balanceSheetResult.columns]);

    return (
        <div className="space-y-8">
            <div className="flex flex-col gap-4">
                <div className="flex items-start justify-between">
                    <div>
                        <h3 className="heading-l mb-2">Summary Financials</h3>
                        <p className="body-s flex items-center gap-1 italic text-gray-400">
                            Data as of Nov 30, 2025
                            <InformationCircleIcon className="size-4 fill-gray-500" />
                        </p>
                    </div>
                </div>
            </div>

            <div id="summary-income-statement" className="space-y-3">
                <div className="flex items-center justify-between">
                    <h4 className="heading-m">Summary Income Statement</h4>
                    <PeriodSelector
                        resolutionAtom={incomeStatementResolutionAtom}
                    />
                </div>
                <MemoTable
                    data={incomeStatementResult.data}
                    columns={incomeStatementColumns}
                    onCellClicked={handleIncomeStatementCellClick}
                />
            </div>

            <div id="summary-balance-sheet" className="space-y-3">
                <div className="flex items-center justify-between">
                    <h4 className="heading-m">Summary Balance Sheet</h4>
                    <PeriodSelector
                        resolutionAtom={balanceSheetResolutionAtom}
                    />
                </div>
                <MemoTable
                    data={balanceSheetResult.data}
                    columns={balanceSheetColumns}
                    onCellClicked={handleBalanceSheetCellClick}
                />
            </div>
        </div>
    );
}
